import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { useSubscription } from '../../contexts/SubscriptionContext';
import './ThankYouPage.css';

/**
 * Thank You Page Component
 * 
 * This component is displayed after a successful Stripe subscription payment.
 * It can be accessed at: /subscription/thank-you
 * 
 * URL Parameters:
 * - session_id: The Stripe checkout session ID (automatically handled)
 * 
 * Features:
 * - Processes successful payments and updates subscription context
 * - Shows processing state while handling payment confirmation
 * - Displays subscription benefits and next steps
 * - Provides navigation to dashboard and subscription management
 * 
 * To test this page manually (for design purposes), you can navigate to:
 * /subscription/thank-you (without session_id parameter)
 */

const ThankYouPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { handlePaymentSuccess } = useSubscription();
  const [isProcessing, setIsProcessing] = useState(!!sessionId);

  useEffect(() => {
    // If we have a session ID, handle the payment success
    if (sessionId) {
      const processPaymentSuccess = async () => {
        try {
          await handlePaymentSuccess();
          console.log('Subscription updated after successful payment');
        } catch (error) {
          console.error('Error updating subscription after payment:', error);
        } finally {
          setIsProcessing(false);
          // Clean the URL to remove the session_id parameter
          window.history.replaceState({}, document.title, '/subscription/thank-you');
        }
      };

      processPaymentSuccess();
    }

    // Optional: Auto-redirect after a certain time
    // const timer = setTimeout(() => {
    //   navigate('/');
    // }, 15000); // Redirect after 15 seconds

    // return () => clearTimeout(timer);
  }, [sessionId, handlePaymentSuccess, navigate]);

  const handleContinue = () => {
    navigate('/');
  };

  return (
    <div className="thank-you-container">
      <div className="thank-you-card">
        {isProcessing ? (
          <>
            <div className="thank-you-icon processing">
              <div className="loading-spinner"></div>
            </div>
            <div className="thank-you-content">
              <h1>Procesando tu suscripción...</h1>
              <p className="thank-you-subtitle">
                Por favor espera mientras confirmamos tu pago
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="thank-you-icon">
              <FaCheckCircle />
            </div>
            
            <div className="thank-you-content">
              <h1>¡Gracias por suscribirte!</h1>
              <p className="thank-you-subtitle">
                Tu suscripción ha sido procesada exitosamente
              </p>
              
              <div className="subscription-details">
                <h3>¿Qué sigue?</h3>
                <ul>
                  <li>Ya tienes acceso completo a todas las funcionalidades de Dentaris</li>
                  <li>Puedes comenzar a gestionar tus pacientes y citas</li>
                  <li>Accede a reportes y herramientas de análisis</li>
                  <li>Disfruta de almacenamiento ilimitado para historiales médicos</li>
                </ul>
              </div>

              <div className="benefits-section">
                <h3>Beneficios de tu suscripción:</h3>
                <div className="benefits-grid">
                  <div className="benefit-item">
                    <h4>📊 Reportes</h4>
                    <p>Análisis de tus actividades</p>
                  </div>
                  <div className="benefit-item">
                    <h4>☁️ Almacenamiento Ilimitado</h4>
                    <p>Guarda todos los historiales sin límites</p>
                  </div>
                  <div className="benefit-item">
                    <h4>🔒 Respaldo Automático</h4>
                    <p>Tus datos están seguros y respaldados</p>
                  </div>
                  <div className="benefit-item">
                    <h4>📞 Manejo de Citas</h4>
                    <p>Calendario para gestión de citas</p>
                  </div>
                </div>
              </div>

              <div className="thank-you-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleContinue}
                >
                  Ir al Dashboard
                  <FaArrowRight />
                </button>
              </div>

              <div className="support-info">
                <p>
                  ¿Tienes preguntas? Contacta nuestro equipo de soporte en{' '}
                  <a href="mailto:soporte@dentaris.com">soporte@dentaris.mx</a>
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ThankYouPage;
